import { getStorageAccessToken } from 'redux/actions/accountActions'
import { logWithTime } from 'util/screenUtils'
import { handleErrors, handleErrorsWithCode } from './AssetController'

export const ENGAGEMENT_TYPES = {
    AUDIO: 0,
    VIDEO: 1
}
export const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=utf-8'
export const JSON_CONTENT_TYPE = 'application/json'

/**
 * In DEVELOPMENT mode, which should normally only be run from localhost,
 * use the stored accessToken because we cannot get the accessToken cookie to
 * be sent back to the server without using https. The Core Service will
 * accept x-access-token header if the origin is the current definition of
 * localhost (currently http://localhost:2429).
 * 
 * In non-DEVELOPMENT mode, set credentials:'include' in fetch options so that
 * the stored accessToken Cookie is included with the request.
 * 
 * ** NOTE ** If you get "Access from [your local computer IP] not allowed" from a mobile device
 * it is probably because you are running a DEVELOPMENT build on that device.
 * 
 * You cannot set contentType on a POST with formData becuase for some reason
 * that blows the server with Entity too large.
 * 
 * @param {*} method GET or POST
 * @param {*} body   For POST, the JSON stringified body
 * @param {*} formUrlEncoded    If true, Content-Type is FORM_CONTENT_TYPE, else JSON_CONTENT_TYPE
 * @returns 
 */
export const getCoreServiceFetchOptions = (method, body, formUrlEncoded) => {
    const contentType = formUrlEncoded ? FORM_CONTENT_TYPE : JSON_CONTENT_TYPE
    switch (method) {
        default:
        case 'GET':
            if (process.env.REACT_APP_MODE === 'development') {
                return {
                    method,
                    headers: {
                        'Content-Type': contentType,
                        'x-access-token': getStorageAccessToken()
                    }
                }
            } else {
                return {
                    method,
                    headers: {
                        'Content-Type': contentType,
                    },
                    credentials: 'include'
                }
            }
        case 'POST':
            if (process.env.REACT_APP_MODE === 'development') {
                if (formUrlEncoded) {
                    return {
                        method,
                        headers: {
                            'x-access-token': getStorageAccessToken()
                        },
                        body
                    }
                } else {
                    return {
                        method,
                        headers: {
                            'Content-Type': contentType,
                            'x-access-token': getStorageAccessToken()
                        },
                        body
                    }
                }
            } else {
                if (formUrlEncoded) {
                    return {
                        method,
                        credentials: 'include',
                        body
                    }
                } else {
                    return {
                        method,
                        headers: {
                            'Content-Type': contentType
                        },
                        credentials: 'include',
                        body
                    }
                }
            }
    }

}

/** Add the home, public and private rooms if they don't exist. In any case,
 * return all studios.
 * 
 * @returns Array of all studios
 */
export const addArtistStudios = async () => {
    logWithTime(`addArtistStudios`)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/addArtistStudios`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((studios) => {
            return studios
        })
}


export const getStudiosForUser = async () => {
    console.log(`getStudiosForUser`)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/getStudios`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((studios) => {
            return studios
        })

}


/**
 * Credits are stored as a Mongoose decimals and so must be converted
 * on the Core Service to an int before returning them here.
 * @returns {availableCredits,availableArtistCredits}
 */
export const getAvailableCredits = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}users/credits/available`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrors)
        .then((res) => res.json())
        .then((creditsResponse) => {
            console.log('Got available credits response', creditsResponse)
            return creditsResponse
        })
}

export const getCreditPricing = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/pricing`
    return fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
        },
    })
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((pricing) => {
            console.log('Got pricing', pricing)
            return pricing
        })

}

/**
 * 
 * @param {*} credit        { accruedCredits, creditsUsed, roomId, userId }
 * @param {*} accessToken 
 * @returns 
 */
export const addPreviewCredit = async (credit) => {
    logWithTime('addPreviewCredit', credit)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/addPreviewCredit`
    return fetch(url, {
        method: 'POST',
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
            'Content-Type': JSON_CONTENT_TYPE,
        },
        body: JSON.stringify(credit),
    })
        .then(handleErrorsWithCode)
        .then(() => {
            return true
        })
}

/**
 * Hey you cannot start a function name with "use" or React thinks it's a hook.
 * @param {*} credit 
 * @returns 
 */
export const applyCredit = async (credit) => {
    logWithTime('applyCredit', credit)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/applyCredit`
    return fetch(url, getCoreServiceFetchOptions('POST', JSON.stringify(credit)))
        .then(handleErrors)
        .then(res => res.json())
        .then((creditsResponse) => {
            return creditsResponse
        })
}

/**
 * Requires Api key
 * 
 * Charge a credit to the owner of the identified Studio. 
 * 
 * *Note* that this is
 * a dangerous call because it is unauthenticated and could result in
 * the Studio owner's credits being lost.

 * @param {*} credit 
 * @param {*} roomId 
 * @returns {*} { _id, availableCredits } where _id is the created Credit id and availableCredits is the remaining amount
 */
export const chargeCreditToStudio = async (credit) => {
    logWithTime('chargeCreditToStudio', credit)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/chargeCreditToStudio`
    return fetch(url, {
        method: 'POST',
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
            'Content-Type': JSON_CONTENT_TYPE,
        },
        body: JSON.stringify(credit),
    })
        .then(handleErrors)
        .then(res => res.json())
        .then((updatedCredit) => {
            return updatedCredit
        })
}


export const getAccruedCredits = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/getAccruedCredits`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((accruedCredits) => {
            return accruedCredits
        })

}

export const getListenersForSong = async (messageId) => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/getListenersForSong/${messageId}`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((songListeners) => {
            return songListeners
        })

}

/**
 * 
 * @returns 
 */
export const getMaximumUpload = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}users/maximumUpload`
    return fetch(url, getCoreServiceFetchOptions('POST'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((uploadResponse) => {
            console.log('getMaximumUpload', uploadResponse)
            return uploadResponse
        })

}

export const getMediaHash = async (source,sourceName) => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}media/getHash`
    return fetch(url, getCoreServiceFetchOptions('POST',
        JSON.stringify({source,sourceName})
    ))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((mediaHash) => {
            console.log('getMediaHash', mediaHash)
            return mediaHash
        })

}
/**
 * Get the Stripe {clientSecret} for the payment by userId on assetId for transactionType. The {paymentData} sent depends
 * on the TRANSACTION_TYPE. When it is room, multiple fields must be sent to create the Room Asset. Otherwise, just
 * the assetId and userId are required to update the database Transaction created by the server-side payment result.

 * @param {*} paymentData           
 * @param {*} transactionType 
 * @returns 
 */
export const getPaymentIntent = async (paymentData) => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}stripe/getPaymentIntent`
    return fetch(url, getCoreServiceFetchOptions('POST', JSON.stringify(paymentData)))
        .then(handleErrors)
        .then((res) => res.json())
        .then((paymentIntent) => {
            console.log('getPaymentIntent received', paymentIntent)
            return paymentIntent
        })
}

/**

 * @param {*} contentUrl The URL to be exploded on the server 
 * @returns 
 */
export const getShareableContent = async (contentUrl) => {
    console.log(`getShareableContent from ${contentUrl}`)
    const url = `${process.env.REACT_APP_CORE_SERVICE}vortex/getShareableContent`
    return fetch(url, getCoreServiceFetchOptions('POST', JSON.stringify(contentUrl)))
        .then(handleErrors)
        .then((res) => res.json())
        .then((shareable) => {
            return shareable
        })
}

export const getSongAccruedCredits = async (messageId, artistUsage, previewUsage) => {
    console.log(`getSongAccruedCredits for ${messageId} artistUsage ${artistUsage} previewUsage ${previewUsage}`)
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/getSongAccruedCredits/${messageId}/${artistUsage ? 1 : 0}/${previewUsage ? 1 : 0}`
    console.log(`...url ${url}`)
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((accruedCredits) => {
            return accruedCredits
        })

}

/**
 * availableCredits is stored as a Mongoose decimal and so it must be converted
 * on the Core Service to an int before returning it here.
 * @param {*} roomId 
 * @returns An integer
 */
export const getStudioCredits = async (roomId) => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}studio/credits/available/${roomId}`
    return fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
        }
    })
        .then(handleErrors)
        .then((res) => res.json())
        .then((availableCredits) => {
            console.log('Got available credits', availableCredits.availableCredits)
            return availableCredits.availableCredits
        })
}


/**
 * @todo Add ApiKey
 * Get the image by the username. This is another reason you can't change username, only handle.
 * @param {*} username
 * @returns { providerImgUrl, img} where img is {data,contentType}
 */
export const getProfileImage = async (username) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/image/${username}`
    )
    if (response.status === 200) {
        const img = await response.json()
        return img
    } else {
        return null
    }
}

/**
 * @todo Add ApiKey
 * @param {*} userId 
 * @returns 
 */
export const getProfileImageById = async (userId) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/imageById/${userId}`
    )
    if (response.status === 200) {
        const img = await response.json()
        return img
    } else {
        return null
    }
}

/**
 * Requires Api key
 * @param {*} username 
 * @returns 
 */
export const getPublicUser = async (username) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/${username}`,
        {
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
            },
        }
    )
    const publicUser = await response.json()
    return { ...publicUser, status: response.status }
}


/**
 * Requires Api key
 * @param {*} handle 
 * @param {*} accessToken 
 * @returns 
 */
export const getPublicUserByHandle = async (handle) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/handle/${handle}`,
        {
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
            },
        }
    )
    const publicUser = await response.json()
    return { ...publicUser, status: response.status }
}

/**
 * Requires Api key
 * @param {*} userId 
 * @returns 
 */
export const getPublicUserById = async (userId) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/id/${userId}`,
        {
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
            },
        }
    )
    const publicUser = await response.json()
    return { ...publicUser, status: response.status }
}

/**
 *
 * @param {*} username
 * @returns
 */
export const getStorage = async (username) => {
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}users/storage/${username}`,
        getCoreServiceFetchOptions('GET'))
    const storage = await response.json()
    return storage
}

export const getSubscribers = async (start, count) => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/getSubscribers/${start}/${count}`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrorsWithCode)
        .then((res) => res.json())
        .then((subscribers) => {
            return subscribers
        })
}
/**
 * All signins depend on Firebase.
 * 
 * @param {*} idtoken
 * @returns
 */
export const googleSignIn = async (idtoken) => {
    let signin = { idtoken, firebase: true }
    const params = new URLSearchParams(
        Object.keys(signin).map((key) => [key, signin[key]])
    )
    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}auth/googlesignin`,
        getCoreServiceFetchOptions('POST', params, true))
    return response
}

export const listMyTransactions = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}transactions/list/0/50`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrors)
        .then((res) => res.json())
        .then((transactions) => {
            return transactions
        })
}

/**
 * @todo This UNAUTHENTICATED call could be used to spam the Core Service
 * @param {*} firstName 
 * @param {*} lastName 
 * @param {*} email 
 * @param {*} message 
 * @returns 
 */
export const sendContactEmail = async (firstName, lastName, email, message) => {
    return fetch(`${process.env.REACT_APP_CORE_SERVICE}contact`,
        {
            method: 'POST',
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY,
                'Content-Type': JSON_CONTENT_TYPE,
            },
            body: JSON.stringify({ firstName, lastName, email, message }),
        })
        .then(handleErrors)
        .then((res) => {
            return res
        })
}

/**
 * @todo Unused
 * @param {*} message 
 * @param {*} sendTo 
 * @returns 
 */
export const sendShareEmailToUser = async (
    message,
    sendTo
) => {
    const data = { message, sendTo }
    return fetch(`${process.env.REACT_APP_CORE_SERVICE}contact/shareRequest`,
        getCoreServiceFetchOptions('POST', JSON.stringify(data)))
        .then(handleErrors)
        .then((res) => {
            return res
        })
}

/**
 * Send seesionStart to get media token cookie. Nope it is never sent blah de dah re CORS
 * but the CORS access policy on the server prevents streaming outside of the authorized Origins.
 * @returns response
 */
export const sessionStart = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}harmonize/sessionStart`
    return fetch(url,
        getCoreServiceFetchOptions('POST'))
        .then(handleErrors)
        .then((res) => {
            return res
        })
}

export const coreServiceSignOut = async () => {
    const url = `${process.env.REACT_APP_CORE_SERVICE}auth/signOut`
    return fetch(url, getCoreServiceFetchOptions('GET'))
        .then(handleErrors)
        .then((res) => {
            return res
        })
}
/**
 * 
 * @param {*} roomId        _id of Room
 * @param {*} subscribe     0 to unsubscribe 1 to subscribe
 * @returns updatedUser
 */
export const updateSubscribed = async (roomId, subscribe) => {

    return fetch(`${process.env.REACT_APP_CORE_SERVICE}users/updateSubscribed`,
        getCoreServiceFetchOptions('POST', JSON.stringify({ roomId, subscribe })))
        .then(handleErrors)
        .then((res) => res.json())
        .then((updatedUser) => {
            return updatedUser
        })
}

/**
 * Update anything in user except img, which this method deletes before sending user.
 * @param {*} user
 * @returns
 */
export const updateUser = async (user) => {
    let safeUser = { ...user }
    delete safeUser.img
    delete safeUser.accessToken
    return fetch(`${process.env.REACT_APP_CORE_SERVICE}users/update`,
        getCoreServiceFetchOptions('POST', JSON.stringify(safeUser)))
        .then(handleErrors)
        .then((res) => res.json())
        .then((updatedUser) => {
            return updatedUser
        })
}

/**
 * This upload sends base64 data to the server
 * @param {*} fileData          Base64 data string
 * @param {*} username      Username who owns the profile
 * @param {*} mimeType      Image mimeType
 * @returns
 */
export const uploadProfileImage = async (
    fileData,
    username,
    mimeType
) => {
    const formData = new FormData()
    formData.append('fileData', fileData)
    formData.append('params', JSON.stringify({ username, mimeType }))

    const response = await fetch(
        `${process.env.REACT_APP_CORE_SERVICE}upload/profile`,
        getCoreServiceFetchOptions('POST', formData, true))
    return response
}
/**
 * 
 * Create a video short using either the image specified by
 * - fileId (Backblaze id), 
 * 
 * or the fileData that will be uploaded as the image for the video.
 * 
 * - audioMedia {source,sourceName}
 * - [options] is {duration,timeStart,textOverlay}
 * 
 * @param {*} fileData  If defined, an array of File objects that are uploaded
 * @param {*} params { name, username, fileId, audioMedia, options }
 * @param {*} accessToken 
 * @returns Blob of video
 * @see https://stackoverflow.com/questions/35206589/how-to-download-fetch-response-in-react-as-file
 */
export const videoShort = async (fileData, params) => {
    console.log(`videoShort params`, params)
    const formData = new FormData()
    if (fileData) {
        console.log('videoShort fileData', fileData)
        fileData.forEach((f, ix) => {
            formData.append(`media${ix}`, f)
        })
    }
    formData.append('params', JSON.stringify(params))
    const url = `${process.env.REACT_APP_CORE_SERVICE}video/short`
    return fetch(url, getCoreServiceFetchOptions('POST', formData, true))
        .then(handleErrors)
        .then((res) => res.blob())
        .then((video) => {
            console.log('Created video short')
            return video
        })
}